import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "wouter";
import { z } from "zod";
import { ShieldAlert, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

// Сообщения схемы — это ключи i18n (namespace auth), переводятся при рендере
// ошибки через t(...), т.к. схема объявлена вне компонента и хук тут недоступен.
const loginSchema = z.object({
  email: z.string().email("errors.emailInvalid"),
  password: z.string().min(6, "errors.passwordMin"),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const { t } = useTranslation("auth");
  const [, navigate] = useLocation();
  const [showBlocked, setShowBlocked] = useState(false);
  const utils = trpc.useUtils();
  const loginMutation = trpc.auth.login.useMutation({
    onError: (err) => {
      if (err.data?.code === "FORBIDDEN" && (err.message.includes("заблокирован") || err.message.toLowerCase().includes("blocked"))) {
        setShowBlocked(true);
      }
    },
    onSuccess: data => {
      const nextUser = data.user
        ? {
          ...data.user,
          isCompetitionOrganizer: data.user.role === "organizer",
          school: data.user.role === "school"
            ? {
              name: data.user.name || "Школа",
              logoUrl: null,
              email: data.user.email ?? null,
            }
            : null,
        }
        : null;
      utils.auth.me.setData(undefined, nextUser as any);
      // Ответ login отдаёт «урезанного» пользователя: без linkedAccount и без
      // реального профиля школы (логотип/email). При staleTime=1000 эти данные
      // ещё секунду считаются свежими, поэтому кабинет успевал отрисоваться
      // без пункта «Перейти в кабинет организатора» — он появлялся только при
      // следующем переходе внутри кабинета. Помечаем me устаревшим сразу,
      // чтобы layout при монтировании дозапросил полный профиль.
      void utils.auth.me.invalidate();

      // Возвращаем пользователя на публичную страницу, с которой он пришёл.
      // Принимаем только внутренние пути, чтобы не создавать open redirect.
      const redirect = new URLSearchParams(window.location.search).get("redirect");
      if (redirect?.startsWith("/") && !redirect.startsWith("//")) {
        navigate(redirect);
        return;
      }

      // Редирект по роли
      const role = data.user?.role;
      if (role === "admin") {
        navigate("/admin");
      } else if (role === "organizer") {
        navigate("/organizer/competitions");
      } else if (role === "school") {
        navigate("/school/profile");
      } else if (role === "athlete") {
        navigate("/athlete");
      } else if (role === "coach") {
        navigate("/coach");
      } else {
      navigate("/dashboard");
      }
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values: LoginForm) => {
    await loginMutation.mutateAsync(values);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/50 to-background flex items-start justify-center px-4 py-8 overflow-y-auto">
      <LanguageSwitcher localOnly className="fixed top-4 right-4 z-50" />
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl">{t("login.title")}</CardTitle>
          <CardDescription>{t("login.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-2">
              <Label htmlFor="email">{t("login.emailLabel")}</Label>
              <Input id="email" type="email" placeholder="you@example.com" {...register("email")} />
              {errors.email && <p className="text-sm text-destructive">{t(errors.email.message!)}</p>}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("login.passwordLabel")}</Label>
                <Link href="/forgot-password" className="text-xs text-primary hover:underline">
                  {t("login.forgotPassword")}
                </Link>
              </div>
              <Input id="password" type="password" placeholder="••••••••" {...register("password")} />
              {errors.password && <p className="text-sm text-destructive">{t(errors.password.message!)}</p>}
            </div>

            {loginMutation.error && !showBlocked && (
              <p className="text-sm text-destructive">{loginMutation.error.message}</p>
            )}

            <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
              {loginMutation.isPending ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>

          {/* Заметный блок регистрации: с публичной страницы сюда попадают
              люди без аккаунта, и мелкая ссылка внизу терялась. */}
          <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-4 text-center space-y-2">
            <p className="text-sm font-medium">{t("login.noAccount")}</p>
            <Link href="/register">
              <Button type="button" variant="outline" className="w-full">
                {t("login.registerLink")}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showBlocked} onOpenChange={setShowBlocked}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="w-5 h-5" />
              {t("login.blocked.title")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>{t("login.blocked.body1")}</p>
            <p>{t("login.blocked.body2")}</p>
            <a
              href="mailto:judixkz@gmail.com"
              className="flex items-center gap-2 text-primary hover:underline font-medium"
            >
              <Mail className="w-4 h-4" />
              judixkz@gmail.com
            </a>
          </div>
          <Button variant="outline" className="w-full mt-2" onClick={() => setShowBlocked(false)}>
            {t("login.blocked.ok")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
